import { useAuth, useTellescopeMutation } from "@tellescope/tanstack-query";
import { Button, Popover, PopoverAnchor, PopoverContent } from "@tellescope/ui";
import { Ban } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { InvalidationReasonSelection } from "../../ContactsDialog/sections/VitalsSection/InvalidationReasonSelection";

export interface VitalsInvalidateActionProps {
  observationIds: string[];
}

export const VitalsInvalidateAction = ({ observationIds }: VitalsInvalidateActionProps) => {
  const { t } = useTranslation("vitals");
  const { selectedOrganization } = useAuth();
  const [showInvalidationReasons, setShowInvalidationReasons] = useState(false);
  const [isInvalidating, setIsInvalidating] = useState(false);

  const updateObservationMutation = useTellescopeMutation({
    model: "enduser_observations",
    method: "updateOne",
  });

  const requireReason = selectedOrganization?.settings?.endusers?.requireObservationInvalidationReason ?? false;

  const invalidateObservations = useCallback(
    async (reason?: string) => {
      setIsInvalidating(true);
      try {
        await Promise.all(
          observationIds.map((id) =>
            updateObservationMutation.mutateAsync({
              id,
              status: "entered-in-error",
              ...(reason ? { invalidationReason: reason } : {}),
            })
          )
        );
        setShowInvalidationReasons(false);
      } finally {
        setIsInvalidating(false);
      }
    },
    [observationIds, updateObservationMutation]
  );

  return (
    <Popover open={showInvalidationReasons} onOpenChange={setShowInvalidationReasons}>
      <PopoverAnchor asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 hover:text-red-600 hover:bg-red-100 transition-colors text-destructive"
          hoverLabel={t("Invalidate")}
          disabled={isInvalidating}
          onClick={(e) => {
            e.stopPropagation();
            if (requireReason) {
              setShowInvalidationReasons(true);
            } else {
              invalidateObservations();
            }
          }}
          data-trackingid="vitals-table-invalidate"
        >
          <Ban className="h-4 w-4" />
        </Button>
      </PopoverAnchor>
      <PopoverContent align="end">
        <InvalidationReasonSelection
          onInvalidate={(reason) => invalidateObservations(reason)}
          onCancel={() => setShowInvalidationReasons(false)}
          invalidating={isInvalidating}
        />
      </PopoverContent>
    </Popover>
  );
};
